import { RelativePathString } from "expo-router";
import { useEffect } from "react";

import { usePropagatedRouter } from "@/hooks/usePropagatedRouter";
import { useAuthState } from "./useAuthState";
import useRedirectUrl from "./useRedirectUrl";

/**
 * Hook that redirects to login if the user is not authenticated.
 * Use this in any component/screen that requires authentication.
 *
 * @param options - Configuration options
 * @param options.enabled - Whether the redirect check is enabled (default: true)
 * @param options.redirectPath - Custom redirect path (default: "/sign-in")
 */
export function useRequireAuth(options?: {
  enabled?: boolean;
  redirectPath?: RelativePathString;
}) {
  const { enabled = true, redirectPath = "/sign-in" as RelativePathString } =
    options || {};
  const { isSignedIn, isLoading, isInitializing } = useAuthState();
  const redirectUrl = useRedirectUrl();
  const router = usePropagatedRouter();
  const isAuthenticated = isSignedIn && !isLoading && !isInitializing;

  useEffect(() => {
    if (isLoading || isInitializing) {
      return;
    }

    if (enabled && !isSignedIn) {
      router.replace({
        pathname: redirectPath,
        params: { redirectUrl },
      });
    }
  }, [
    enabled,
    isSignedIn,
    isLoading,
    isInitializing,
    redirectPath,
    redirectUrl,
    router,
  ]);

  return {
    isAuthenticated,
    isLoading: (isLoading || isInitializing) && enabled,
  };
}
